/**
 * Webhooks Page - Manage webhook endpoints
 */
import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  MoreVertical,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  BarChart,
  Download,
  Upload,
  Copy,
  RefreshCw,
  AlertTriangle,
  Calendar,
  Activity,
  ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { webhooksApi, Webhook, WebhookTestResult } from '../services/webhooksApi';

const Webhooks: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'paused'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    method: 'POST' as 'POST' | 'GET' | 'PUT' | 'PATCH',
    headers: '{}',
    secret: '',
  });

  useEffect(() => {
    loadWebhooks();
  }, [statusFilter]);

  const loadWebhooks = async () => {
    setLoading(true);
    try {
      const data = await webhooksApi.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: searchTerm || undefined,
      });
      setWebhooks(data);
    } catch (error) {
      console.error('Error loading webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let headers = {};
      try {
        headers = JSON.parse(formData.headers);
      } catch (err) {
        alert('Headers inválidos. Use formato JSON válido.');
        return;
      }

      await webhooksApi.create({
        name: formData.name,
        description: formData.description || undefined,
        url: formData.url,
        method: formData.method,
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        secret: formData.secret || undefined,
      });

      setShowCreateModal(false);
      resetForm();
      loadWebhooks();
    } catch (error) {
      console.error('Error creating webhook:', error);
      alert('Erro ao criar webhook. Verifique os dados e tente novamente.');
    }
  };

  const handleToggleStatus = async (webhook: Webhook) => {
    try {
      if (webhook.status === 'active') {
        await webhooksApi.pause(webhook.id);
      } else {
        await webhooksApi.activate(webhook.id);
      }
      loadWebhooks();
    } catch (error) {
      console.error('Error toggling webhook status:', error);
    }
  };

  const handleTestWebhook = async () => {
    if (!selectedWebhook) return;

    setTesting(true);
    setTestResult(null);

    try {
      const result = await webhooksApi.test(selectedWebhook.id, {
        test: true,
        timestamp: new Date().toISOString(),
      });
      setTestResult(result);
    } catch (error) {
      console.error('Error testing webhook:', error);
      alert('Erro ao testar webhook');
    } finally {
      setTesting(false);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    alert('URL copiada para a área de transferência!');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      url: '',
      method: 'POST',
      headers: '{}',
      secret: '',
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      paused: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };

    const icons = {
      active: <CheckCircle className="w-4 h-4" />,
      inactive: <XCircle className="w-4 h-4" />,
      paused: <Pause className="w-4 h-4" />,
    };

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${styles[status as keyof typeof styles]}`}>
        {icons[status as keyof typeof icons]}
        {status === 'active' ? 'Ativo' : status === 'inactive' ? 'Inativo' : 'Pausado'}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid var(--glass-border)',
          borderTopColor: 'var(--color-primary-600)',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      {/* Back to Settings */}
      <button
        onClick={() => navigate('/settings')}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
          padding: 'var(--space-1) 0', marginBottom: 'var(--space-4)',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary-600)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
      >
        <ArrowLeft size={16} />
        Voltar para Configurações
      </button>

      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>Webhooks</h1>
        <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-secondary)' }}>
          Configure endpoints para receber dados de sistemas externos
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-glass)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Total de Webhooks</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{webhooks.length}</p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'var(--color-primary-50)', borderRadius: 'var(--radius-lg)' }}>
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-glass)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Ativos</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>
                {webhooks.filter((w) => w.status === 'active').length}
              </p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'var(--color-success-50)', borderRadius: 'var(--radius-lg)' }}>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-glass)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Pausados</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>
                {webhooks.filter((w) => w.status === 'paused').length}
              </p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'var(--color-warning-50)', borderRadius: 'var(--radius-lg)' }}>
              <Pause className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-glass)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Total de Triggers</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>
                {webhooks.reduce((sum, w) => sum + w.trigger_count, 0)}
              </p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'rgba(139, 92, 246, 0.1)', borderRadius: 'var(--radius-lg)' }}>
              <BarChart className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-4)', marginBottom: 'var(--space-6)', boxShadow: 'var(--shadow-glass)' }}>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar webhooks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
              <option value="paused">Pausados</option>
            </select>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Novo Webhook
            </button>
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', boxShadow: 'var(--shadow-glass)', overflow: 'hidden' }}>
        {webhooks.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Activity className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum webhook encontrado</h3>
            <p className="text-gray-600 mb-4">
              {statusFilter === 'all'
                ? 'Comece criando seu primeiro webhook'
                : 'Não há webhooks com esse status'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Criar Webhook
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Método
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Triggers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sucesso/Error
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Trigger
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {webhooks.map((webhook) => (
                <tr key={webhook.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{webhook.name}</div>
                      {webhook.description && (
                        <div className="text-sm text-gray-500">{webhook.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(webhook.status)}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                      {webhook.method}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                    {webhook.trigger_count}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 text-sm font-medium">{webhook.success_count}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-red-600 text-sm font-medium">{webhook.error_count}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {webhook.last_triggered_at ? (
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        {new Date(webhook.last_triggered_at).toLocaleString('pt-BR')}
                      </div>
                    ) : (
                      <span className="text-gray-400">Nunca</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedWebhook(webhook);
                          setShowUrlModal(true);
                        }}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Ver URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedWebhook(webhook);
                          setShowTestModal(true);
                          setTestResult(null);
                        }}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Testar"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedWebhook(webhook);
                          setShowStatsModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Estatísticas"
                      >
                        <BarChart className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(webhook)}
                        className={`p-2 rounded-lg transition-colors ${
                          webhook.status === 'active'
                            ? 'text-yellow-600 hover:bg-yellow-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={webhook.status === 'active' ? 'Pausar' : 'Ativar'}
                      >
                        {webhook.status === 'active' ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info Banner */}
      <div style={{ marginTop: 'var(--space-6)', padding: 'var(--space-4)', background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)', border: '1px solid var(--color-primary-200)', borderRadius: 'var(--radius-xl)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <AlertTriangle className="w-5 h-5 text-blue-600" style={{ marginTop: '2px' }} />
          <div>
            <h4 style={{ fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px' }}>Sobre Webhooks</h4>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              Webhooks permitem que sistemas externos enviem dados para o FLIPR em tempo real. Use-os para integrar
              com formulários, sistemas de pagamento, ferramentas de marketing e muito mais.
            </p>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Criar Novo Webhook</h2>
            </div>
            <form onSubmit={handleCreateWebhook} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Integração Google Forms"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  placeholder="Descrição opcional do webhook"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL do Endpoint * (gerado automaticamente)
                </label>
                <div className="bg-gray-50 px-4 py-2 rounded-lg text-sm text-gray-600">
                  Será gerada automaticamente após criar o webhook
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Método HTTP *
                </label>
                <select
                  value={formData.method}
                  onChange={(e) => setFormData({ ...formData, method: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Headers Adicionais (JSON)
                </label>
                <textarea
                  value={formData.headers}
                  onChange={(e) => setFormData({ ...formData, headers: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  rows={3}
                  placeholder='{"Authorization": "Bearer token"}'
                />
                <p className="text-xs text-gray-500 mt-1">Use formato JSON válido para headers adicionais</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret (Opcional - para validação de segurança)
                </label>
                <input
                  type="text"
                  value={formData.secret}
                  onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  placeholder="Chave secreta para validar requisições"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usado para validar assinaturas de webhooks recebidos
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Criar Webhook
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* URL Modal */}
      {showUrlModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">URL do Webhook</h2>
              <p className="text-sm text-gray-600 mt-1">{selectedWebhook.name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Pública
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`https://api.flipr.io/webhooks/${selectedWebhook.id}/`}
                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm"
                  />
                  <button
                    onClick={() => handleCopyUrl(`https://api.flipr.io/webhooks/${selectedWebhook.id}/`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use esta URL para enviar dados de sistemas externos
                </p>
              </div>

              {selectedWebhook.secret && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secret
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={selectedWebhook.secret}
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                    <button
                      onClick={() => handleCopyUrl(selectedWebhook.secret!)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Use este secret para validar assinaturas de requisições
                  </p>
                </div>
              )}

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">Exemplo de Payload</h4>
                <pre className="text-xs text-gray-800 bg-white p-3 rounded overflow-x-auto">
{`{
  "event_type": "lead.created",
  "data": {
    "name": "João Silva",
    "email": "joao@email.com",
    "phone": "+55 11 99999-9999"
  },
  "timestamp": "${new Date().toISOString()}"
}`}
                </pre>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowUrlModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Testar Webhook</h2>
              <p className="text-sm text-gray-600 mt-1">{selectedWebhook.name}</p>
            </div>
            <div className="p-6 space-y-4">
              <button
                onClick={handleTestWebhook}
                disabled={testing}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                {testing ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Testando...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Enviar Teste
                  </>
                )}
              </button>

              {testResult && (
                <div className="space-y-3">
                  <div className={`p-4 rounded-lg ${
                    testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {testResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-medium ${
                        testResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {testResult.success ? 'Sucesso' : 'Erro'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">
                      Status Code: <span className="font-mono">{testResult.status_code}</span>
                    </p>
                    <p className="text-sm text-gray-700">
                      Tempo: <span className="font-mono">{testResult.processing_time_ms}ms</span>
                    </p>
                    {testResult.error && (
                      <p className="text-sm text-red-700 mt-2">{testResult.error}</p>
                    )}
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Resposta</h4>
                    <pre className="text-xs text-gray-800 bg-gray-50 p-3 rounded overflow-x-auto max-h-64">
                      {JSON.stringify(testResult.response, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowTestModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal placeholder */}
      {showStatsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Estatísticas</h2>
              <p className="text-sm text-gray-600 mt-1">Funcionalidade em desenvolvimento</p>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <BarChart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">
                  Estatísticas detalhadas serão implementadas na próxima etapa
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowStatsModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Webhooks;
